import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiFetch } from "../../api/client.ts";

interface SearchResults {
  leads: Array<{ id: string; name: string | null; phone: string | null; service_type: string | null }>;
  sites: Array<{ id: string; name: string; domain: string }>;
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 30);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!open || q.length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      apiFetch<SearchResults>(`/search?q=${encodeURIComponent(q)}`).then(setResults).catch(() => setResults(null));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) {
    return (
      <button type="button" className="btn-secondary" onClick={() => setOpen(true)} title="⌘K">
        <Search className="h-4 w-4" /> Search
      </button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-24" onClick={() => setOpen(false)}>
      <div className="w-full max-w-xl rounded-lg bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="input"
          placeholder="Search leads, sites, clients…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {results && (
          <div className="mt-3 space-y-3 text-sm">
            {results.leads.length > 0 && (
              <section>
                <h4 className="mb-1 text-xs uppercase text-slate-500">Leads</h4>
                <ul className="divide-y divide-slate-100">
                  {results.leads.map((l) => (
                    <li
                      key={l.id}
                      className="cursor-pointer py-2 hover:bg-slate-50"
                      onClick={() => {
                        nav(`/leads/${l.id}`);
                        setOpen(false);
                      }}
                    >
                      <div>{l.name ?? "(no name)"}</div>
                      <div className="text-xs text-slate-500">
                        {l.phone ?? ""} • {l.service_type ?? ""}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {results.sites.length > 0 && (
              <section>
                <h4 className="mb-1 text-xs uppercase text-slate-500">Sites</h4>
                <ul className="divide-y divide-slate-100">
                  {results.sites.map((s) => (
                    <li
                      key={s.id}
                      className="cursor-pointer py-2 hover:bg-slate-50"
                      onClick={() => {
                        nav(`/sites`);
                        setOpen(false);
                      }}
                    >
                      <div>{s.name}</div>
                      <div className="text-xs text-slate-500">{s.domain}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
